import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaDownload } from 'react-icons/fa';

import getAPIClient from '../hooks/apiClient';
import InlineNotification from './InlineNotification';
import OneSelectUI from './inputs/one-select';
import s from './HhVacancyImport.module.scss';


export default function HhVacancyImport({ BX24 }) {
    const api = useMemo(() => getAPIClient(BX24), []);

    const [url, setUrl] = useState('');
    const [importing, setImporting] = useState(false);
    const [notificationType, setNotificationType] = useState('info');
    const [notificationText, setNotificationText] = useState('');
    /** @type {[{crm_item_id: number, b24_entity_type_id: number, vacancy: object}|null, Function]} */
    const [lastResult, setLastResult] = useState(null);

    /** @type {[Array<{direction_id: number, direction_name: string, is_default: boolean}>, Function]} */
    const [categories, setCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [categoriesError, setCategoriesError] = useState('');
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);

    const portalLink = useMemo(() => {
        try {
            const params = BX24?.getAllParams?.() || {};
            if (params.DOMAIN) return `https://${params.DOMAIN}`;
        } catch (_) {}
        return null;
    }, [BX24]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setCategoriesLoading(true);
            setCategoriesError('');
            try {
                const response = await api.get('/analysis_agent/vacancies/categories');
                if (cancelled) return;
                const list = Array.isArray(response.data) ? response.data : [];
                setCategories(list);
                const defaultItem = list.find(c => c.is_default) || list[0];
                if (defaultItem) setSelectedCategoryId(defaultItem.direction_id);
            } catch (e) {
                if (cancelled) return;
                setCategoriesError(e?.response?.data?.detail || 'Не удалось загрузить список воронок');
            } finally {
                if (!cancelled) setCategoriesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [api]);

    const categoryOptions = useMemo(
        () => categories.map(c => ({ value: c.direction_id, label: c.direction_name })),
        [categories]
    );
    const selectedOption = useMemo(
        () => categoryOptions.find(o => o.value === selectedCategoryId) || null,
        [categoryOptions, selectedCategoryId]
    );

    const runImport = useCallback(async () => {
        const trimmed = url.trim();
        if (!trimmed) {
            setNotificationType('error');
            setNotificationText('Введите ссылку на вакансию hh.ru');
            return;
        }
        setImporting(true);
        setNotificationText('');
        try {
            const payload = { url: trimmed };
            if (selectedCategoryId !== null && selectedCategoryId !== undefined) {
                payload.category_id = selectedCategoryId;
            }
            const response = await api.post('/analysis_agent/vacancies/import/hh', payload);
            setLastResult(response.data || null);
            setNotificationType('success');
            setNotificationText('Вакансия импортирована и создана в Bitrix24');
            setUrl('');
        } catch (e) {
            setNotificationType('error');
            setNotificationText(e?.response?.data?.detail || 'Не удалось импортировать вакансию с hh.ru');
        } finally {
            setImporting(false);
        }
    }, [api, url, selectedCategoryId]);

    const openInBitrix = () => {
        if (!lastResult || !portalLink) return;
        const path = `/crm/type/${lastResult.b24_entity_type_id || ''}/details/${lastResult.crm_item_id}/`;
        const fullUrl = `${portalLink}${path}`;
        if (typeof BX24?.openPath === 'function') {
            BX24.openPath(path);
        } else {
            window.open(fullUrl, '_blank', 'noopener,noreferrer');
        }
    };

    const showCategorySelect = !categoriesError && (categoriesLoading || categories.length > 1);

    return (
        <div className={s.container}>
            <h2 className={s.title}>Импорт вакансии с hh.ru</h2>
            <p className={s.hint}>
                Вставьте ссылку на вакансию hh.ru — мы создадим карточку в настроенном смарт-процессе и
                автоматически заполним название, описание и список компетенций. Ограничения остаются ручными:
                их можно настроить на вкладке «Требования вакансии» внутри карточки.
            </p>

            {showCategorySelect && (
                <OneSelectUI
                    title="Воронка"
                    selectionOptions={categoryOptions}
                    selectedOption={selectedOption}
                    setSelectedOption={(opt) => setSelectedCategoryId(opt ? opt.value : null)}
                    isLoading={categoriesLoading}
                    isDisabled={categoriesLoading || importing}
                    placeholder="Выберите воронку…"
                />
            )}

            {categoriesError && (
                <InlineNotification type="error" text={categoriesError} />
            )}

            <div className={s.row}>
                <input
                    type="text"
                    className={s.input}
                    placeholder="https://hh.ru/vacancy/12345678"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={importing}
                />
                <button
                    type="button"
                    className={s.button}
                    onClick={runImport}
                    disabled={importing || !url.trim() || categoriesLoading}
                >
                    <FaDownload /> {importing ? 'Импорт…' : 'Импортировать'}
                </button>
            </div>

            <InlineNotification
                type={notificationType}
                text={notificationText}
                hideFlag={!notificationText}
            />

            {lastResult && (
                <div className={s.resultCard}>
                    <div className={s.resultTitle}>Создано:</div>
                    <div className={s.resultName}>{lastResult.vacancy?.name || '—'}</div>
                    <div className={s.resultMeta}>
                        <span>CRM ID: {lastResult.crm_item_id}</span>
                        {lastResult.vacancy?.hh_id && <span>hh.ru ID: {lastResult.vacancy.hh_id}</span>}
                    </div>
                    <div className={s.actions}>
                        <button type="button" className={s.secondaryButton} onClick={openInBitrix}>
                            Открыть в Bitrix24
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}